/**
 * Windowing — render the rows a scroll box can actually show, and none of the others.
 *
 * **The arithmetic is pure and the measuring is a hook**, split that way for the reason
 * `lib/grid-interop/clipboard.ts` is split: which rows to draw is the part with rules in it,
 * and a rule that lives inside a component is a rule nobody can exercise. `windowOf` reads no
 * DOM and holds no state — a scroll offset, a viewport and a row height in, a pair of indices
 * and two spacer heights out.
 *
 * **Fixed row height, deliberately.** A measuring virtualiser — one that lets every row be
 * whatever height it turns out to be — is a library (`MCK-20260521-002` is where a dependency
 * of that size gets decided, not a component file). A list whose rows are one line each does
 * not need one: the position of row *n* is `n × height` and there is nothing to measure. So
 * this module is for lists that pin their row height, and it is the consumer's job to actually
 * pin it in CSS — see `Slicer.module.css`'s `--slicer-row-height`, which is the twin of the
 * number its component passes in here (retro rule 12).
 *
 * **Two spacers, not absolute positioning.** The rows stay in the normal flow with an empty
 * box above and below standing in for what is not drawn. That keeps the scrollbar honest, and
 * it is the only form that works inside a `<table>`, where `contain` — and therefore
 * `content-visibility` — does not apply to rows at all.
 */

import { useEffect, useState } from "react";

/** How many rows beyond the viewport are drawn on each side, so a scroll is not a blank. */
const OVERSCAN = 6;

export interface ListWindow {
  /** Index of the first row drawn. */
  readonly first: number;
  /** Index **after** the last row drawn — a `slice` bound, not an inclusive one. */
  readonly last: number;
  /** Pixels of empty box above the drawn rows. */
  readonly above: number;
  /** Pixels of empty box below them. */
  readonly below: number;
}

/**
 * Which rows to draw.
 *
 * `keep` is an index that must be drawn whatever the scroll offset says — the active row of an
 * `aria-activedescendant` list, whose whole contract is that the id it names exists in the
 * document. A window that scrolled past it would leave the attribute pointing at nothing,
 * which is the failure mode assistive technology cannot report and a mouse user cannot see.
 * `-1` means there is no such row.
 *
 * A `keep` that lands outside the scrolled window **moves** the window rather than stretching
 * it: stretching would draw every row between here and there, which is the one outcome this
 * function exists to prevent.
 */
export function windowOf(
  count: number,
  rowHeight: number,
  scrollTop: number,
  viewport: number,
  keep = -1,
): ListWindow {
  if (count <= 0 || rowHeight <= 0) return { first: 0, last: 0, above: 0, below: 0 };

  const span = Math.max(1, Math.ceil(viewport / rowHeight)) + OVERSCAN * 2;
  let first = Math.max(0, Math.floor(scrollTop / rowHeight) - OVERSCAN);
  let last = Math.min(count, first + span);

  if (keep >= 0 && (keep < first || keep >= last)) {
    first = Math.max(0, Math.min(keep - OVERSCAN, count - span));
    last = Math.min(count, first + span);
  }

  return {
    first,
    last,
    above: first * rowHeight,
    below: (count - last) * rowHeight,
  };
}

export interface WindowedList {
  /** Attach to the scrolling box. A callback ref, so the effect re-runs when it appears. */
  readonly ref: (node: HTMLElement | null) => void;
  /** The box itself, for a consumer that needs to scroll it — see `Slicer`'s active row. */
  readonly box: HTMLElement | null;
  readonly window: ListWindow;
}

/**
 * The same, measured against a real scroll box.
 *
 * The element is held in **state** rather than in a ref for the reason `TreeTable`'s `frame`
 * is: a ref would be read once by an effect with no dependency to re-run on, so a list that
 * mounts empty and fills a moment later would be measured against nothing and stay wrong for
 * the rest of the session. A callback ref in state re-runs the effect whenever the node
 * appears or goes.
 *
 * `ResizeObserver` beside the scroll listener because the viewport is half the answer: a rail
 * that grows when the window does must draw more rows, and no scroll event says so.
 */
export function useListWindow(
  count: number,
  rowHeight: number,
  keep = -1,
): WindowedList {
  const [box, setBox] = useState<HTMLElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(0);

  useEffect(() => {
    if (box === null) return undefined;
    const read = (): void => {
      setScrollTop(box.scrollTop);
      setViewport(box.clientHeight);
    };
    read();
    box.addEventListener("scroll", read, { passive: true });
    const observer = new ResizeObserver(read);
    observer.observe(box);
    return () => {
      box.removeEventListener("scroll", read);
      observer.disconnect();
    };
  }, [box]);

  return {
    ref: setBox,
    box,
    window: windowOf(count, rowHeight, scrollTop, viewport, keep),
  };
}

/**
 * The nearest ancestor that actually scrolls, or `null` for the page itself.
 *
 * A list that owns its scroll box measures the box (`useListWindow`). A **table** usually does
 * not: `components/screen`'s `.body` is what scrolls on a desktop, and below 900px it stops
 * scrolling and the page takes over. So the table finds its box at runtime rather than being
 * told, and a `null` here is not a failure — it is the ordinary answer on a phone.
 */
function scrollBoxOf(node: HTMLElement): HTMLElement | null {
  for (let parent = node.parentElement; parent !== null; parent = parent.parentElement) {
    const overflow = getComputedStyle(parent).overflowY;
    if (overflow === "auto" || overflow === "scroll" || overflow === "overlay") {
      return parent;
    }
  }
  return null;
}

/**
 * The same window, for rows that live inside somebody else's scroll box.
 *
 * `useListWindow` asks the box how far it has scrolled. That question has no answer here,
 * because the rows do not start at the top of the box — a table sits under a toolbar, a chip
 * strip and its own `<thead>`. So the measurement is the other way round: **how much of the
 * anchor has gone past the top of the box**, read off one `getBoundingClientRect` per scroll.
 * Negative — the rows are still below the fold — means nothing has scrolled away, hence the
 * clamp at zero.
 *
 * The total height the rows occupy never changes as the window moves (the two spacers give
 * back exactly what the undrawn rows would have taken), so the anchor's own top is stable and
 * this cannot feed back into itself.
 *
 * `resize` as well as `scroll`, and a `ResizeObserver` on the box: the viewport is half the
 * answer, and neither a window resize nor a rail opening beside the table fires a scroll.
 */
export function useAnchoredWindow(
  count: number,
  rowHeight: number,
  keep = -1,
): { readonly ref: (node: HTMLElement | null) => void; readonly window: ListWindow } {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [seen, setSeen] = useState({ scrolled: 0, viewport: 0 });

  useEffect(() => {
    if (anchor === null) return undefined;
    const box = scrollBoxOf(anchor);
    const read = (): void => {
      const top = anchor.getBoundingClientRect().top;
      const boxTop = box === null ? 0 : box.getBoundingClientRect().top;
      const viewport = box === null ? window.innerHeight : box.clientHeight;
      const scrolled = Math.max(0, boxTop - top);
      // Only when it actually moved. A fresh object every scroll event would re-render the
      // whole table sixty times a second to arrive at the same two numbers.
      setSeen((was) =>
        was.scrolled === scrolled && was.viewport === viewport
          ? was
          : { scrolled, viewport },
      );
    };
    read();
    const scroller: EventTarget = box ?? window;
    scroller.addEventListener("scroll", read, { passive: true });
    window.addEventListener("resize", read);
    const observer = new ResizeObserver(read);
    observer.observe(box ?? document.documentElement);
    return () => {
      scroller.removeEventListener("scroll", read);
      window.removeEventListener("resize", read);
      observer.disconnect();
    };
  }, [anchor]);

  return {
    ref: setAnchor,
    window: windowOf(count, rowHeight, seen.scrolled, seen.viewport, keep),
  };
}

/**
 * Put row `index` on screen by moving the box the least it can move — `block: "nearest"`, done
 * by arithmetic because the row may not be in the document to call `scrollIntoView` on.
 *
 * That is the whole reason this is not `element.scrollIntoView()`: in a windowed list the row
 * a keyboard just moved to is frequently one that is not drawn yet, and the position of a row
 * that does not exist is still exactly `index × height`.
 */
export function scrollRowIntoView(
  box: HTMLElement | null,
  index: number,
  rowHeight: number,
): void {
  if (box === null || index < 0) return;
  const top = index * rowHeight;
  if (top < box.scrollTop) box.scrollTop = top;
  else if (top + rowHeight > box.scrollTop + box.clientHeight)
    box.scrollTop = top + rowHeight - box.clientHeight;
}
